import type React from "react"

import { HStack } from "@/nzds/layout"

import { Button, Typography } from "@mui/material"

import { StackedBarGraphSwatch } from "./stacked-bar-graph-swatch"
import type { StackedBarGraphSeries } from "./stacked-bar-graph.types"

interface StackedBarGraphLegendItem extends StackedBarGraphSeries {
  color: string
}

interface StackedBarGraphLegendProps {
  items: StackedBarGraphLegendItem[]
  onSelectedSeriesKeyChange?: (key: string | null) => void
  selectedSeriesKey?: string | null
}

const nextSelectedSeriesKey = (
  clickedKey: string,
  selectedSeriesKey: string | null | undefined
): string | null => (clickedKey === selectedSeriesKey ? null : clickedKey)

const legendItemButtonBackground = (
  isSelected: boolean
): "action.selected" | "transparent" =>
  isSelected ? "action.selected" : "transparent"

// While one series is isolated the unselected items are dimmed, so the chart
// and any PNG of it read as "the others are hidden" rather than "the others
// reported nothing". They stay visible and clickable.
const legendItemOpacity = (
  isSelected: boolean,
  hasSelection: boolean
): number => (!hasSelection || isSelected ? 1 : 0.45)

export const StackedBarGraphLegend: React.FC<StackedBarGraphLegendProps> = ({
  items,
  onSelectedSeriesKeyChange,
  selectedSeriesKey,
}) => (
  <HStack
    component="ul"
    data-testid="stacked-bar-graph-legend"
    sx={{
      columnGap: 2,
      flexWrap: "wrap",
      justifyContent: "flex-start",
      listStyle: "none",
      m: 0,
      px: 0,
      py: 1,
      rowGap: 1,
    }}
  >
    {items.map((item) => {
      const isSelected = item.key === selectedSeriesKey
      const hasSelection = selectedSeriesKey != null

      return (
        <HStack key={item.key} component="li" sx={{ alignItems: "center" }}>
          <Button
            aria-pressed={isSelected}
            onClick={() => {
              onSelectedSeriesKeyChange?.(
                nextSelectedSeriesKey(item.key, selectedSeriesKey)
              )
            }}
            size="small"
            sx={{
              bgcolor: legendItemButtonBackground(isSelected),
              color: "text.primary",
              minWidth: 0,
              opacity: legendItemOpacity(isSelected, hasSelection),
              px: 0.75,
              py: 0.25,
              textTransform: "none",
            }}
          >
            <StackedBarGraphSwatch color={item.color} />
            <Typography
              sx={{ fontWeight: isSelected ? "fontWeightMedium" : undefined }}
              variant="caption"
            >
              {item.label}
            </Typography>
          </Button>
        </HStack>
      )
    })}
  </HStack>
)
